import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "./authSlice";

const baseURL = import.meta.env.VITE_API_BASE_URL;

export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async ({ page = 1, perPage = 10, search = "" }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/admin/user-list`, {
        params: {
          page,
          per_page: perPage,
          search,
        },
      });

      return {
        users: res.data.data.users,
        current_page: res.data.data.current_page,
        total_pages: res.data.data.total_pages,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateUserStatus = createAsyncThunk(
  "users/updateUserStatus",
  async ({ user_id, is_confirmed }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/admin/user-status-update", {
        user_id,
        is_confirmed,
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const verifyUsers = createAsyncThunk(
  "users/verifyUsers",
  async ({ user_id, is_confirmed }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/admin/user-verify-status-update", {
        user_id,
        is_confirmed,
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const redirectUser = createAsyncThunk(
  "Users/redirectUser",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        "/admin/admin-login-as-user",
        payload,
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Redirect failed");
    }
  },
);

// User Slice
const userSlice = createSlice({
  name: "userlist",
  initialState: {
    data: [],
    currentPage: 1,
    totalPages: 1,
    status: "idle",
    error: null,
  },
  reducers: {
    toggleUserStatus: (state, action) => {
      const user = state.data.find((u) => u.id === action.payload);
      if (user) user.status = !user.status;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload.users.map((u) => {
          const [datePart, timePart, meridiem] = u.user_created_at.split(" ");

          // Handle last login
          let lastLoginDate = "";
          let lastLoginTime = "";

          if (u.last_app_logged_at) {
            const parts = u.last_app_logged_at.split(" ");
            lastLoginDate = parts[0];
            lastLoginTime = parts[1] + " " + parts[2];
          }

          return {
            id: u.user_id,
            detail: {
              name: u.user_name,
              mobile: u.user_contact_no,
              email: u.user_email,
            },
            status: u.user_status === 1,
            is_verify: u.is_verify || 0, // ✅ Add is_verify from API
            location: u.user_address,
            date: datePart,
            time: `${timePart} ${meridiem}`,
            total_orders: Number(u.total_orders) || 0,
            total_meals: Number(u.total_meals) || 0,
            total_customers: Number(u.total_customers) || 0,
            encrypted_id: u.user_encrypted_id,
            providers_link: u.user_service_qr_link,
            last_app_logged_in: {
              date: lastLoginDate,
              time: lastLoginTime,
            },
          };
        });

        state.currentPage = action.payload.current_page;
        state.totalPages = action.payload.total_pages;
        state.entriesPerPage = action.payload.per_page_records;
      })

      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(updateUserStatus.fulfilled, (state, action) => {
        const { user_id, new_status } = action.payload.data;
        const user = state.data.find((u) => u.id === user_id);
        if (user) user.status = new_status === 1;
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ✅ Handle verifyUsers response if needed
      .addCase(verifyUsers.fulfilled, (state, action) => {
        // You can update the state here if needed
        // const { user_id, is_confirmed } = action.payload.data;
        // const user = state.data.find((u) => u.id === user_id);
        // if (user) user.is_verify = is_confirmed;
      })
      .addCase(verifyUsers.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { toggleUserStatus } = userSlice.actions;
export default userSlice.reducer;
